import React, { PropsWithChildren, ComponentProps, forwardRef } from 'react';
import { type VariantProps } from 'class-variance-authority';

import { default as NextLink } from 'next/link';

import getCtaStyles from '../button/get-cta-styles';

export interface ButtonProps
	extends ComponentProps<typeof NextLink>,
		VariantProps<typeof getCtaStyles> {
	icon?: React.ReactNode;
	iconPosition?: 'start' | 'end';
	isLoading?: boolean;
}

const Link = forwardRef<HTMLElement, PropsWithChildren<ButtonProps>>(
	(
		{
			href,
			icon,
			iconPosition = 'end',
			isLoading = false,
			className,
			intent = 'text',
			size,
			inverse,
			children,
			prefetch,
			...props
		},
		ref
	) => {
		const hasIcon = Boolean(icon);

		const classNames = getCtaStyles({
			intent,
			isLoading,
			size,
			hasIcon,
			iconPosition,
			inverse,
			className,
		});

		// External links → <a>
		if (typeof href === 'string' && ['http', 'tel', 'mailto'].some(a => href.startsWith(a))) {
			return (
				<a ref={ref as React.Ref<HTMLAnchorElement>} href={href} className={classNames} {...props}>
					{children}
					{icon}
				</a>
			);
		}

		// Internal links → Next.js <Link>
		return (
			<NextLink
				ref={ref as React.Ref<HTMLAnchorElement>}
				href={href}
				className={classNames}
				prefetch={prefetch}
				{...props}
			>
				{children}
				{icon}
			</NextLink>
		);
	}
);

Link.displayName = 'Link';

export default Link;
